import { useQuery } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CommercialAirlineNavigationBar from "../../../../components/element/CommercialAirlineNavigationBar";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import ROUTE_PATHS from "../../../../utils/routePaths";
import TripDetailsTable from "../Tables/TripDetailsTable";
import "./TripDetails.css";
import TripDetailsApprovedView from "./TripDetailsApprovedView";
import TripDetailsPaymentSummary from "./TripDetailsPaymentSummary";
import TripDetailsSpecialOnboarding from "./TripDetailsSpecialOnboarding";
import { useFormData } from "../../../../helpers/FormDataProvider";
import { ClearanceStatus, DisputeStatus } from "../../../../utils/constants";
import { getAirlineName, getFlightNumber } from "../../../../utils/utils";
import { StorageContext } from "../../../../services/StorageProvider";
import { useConfiguration } from "../../../../services/ConfigurationProvider";
import {
  getDiplomatsPassenger,
  getGovernmentPassenger,
  getMilitaryPassenger,
  getInfantPassenger,
} from "../../../../utils/configUtils";
import VerificationIcon from "../../../../components/element/VerificationIcon";

const TripDetails = () => {
  const { userData } = useFormData();
  const { airlineData, airportData } = useContext(StorageContext);
  const { fetchCommercialFlightPlanApprovals, fetchCommercialEscort } =
    useAirlineAdminApi(userData.isAAUser);
  const [flightPlanDetail, setFlightPlanDetail] = useState(null);
  const [editedFlightDetails, setEditedFlightDetails] = useState({});
  const navigate = useNavigate();

  const { configurationData } = useConfiguration();

  const governmentPassengerEnabled = getGovernmentPassenger(configurationData);
  const diplomatsPassengerEnabled = getDiplomatsPassenger(configurationData);
  const militaryPassengerEnabled = getMilitaryPassenger(configurationData);
  const infantPassengerEnabled = getInfantPassenger(configurationData);

  const { data: commercialEscortService } = useQuery({
    queryKey: [
      "get-commercial-flight-plan-escort-service",
      flightPlanDetail?.id,
    ],
    queryFn: () => fetchCommercialEscort(flightPlanDetail?.id),
    enabled: !!flightPlanDetail?.id,
  });
  const { data: flightPlanApprovals } = useQuery({
    queryKey: ["get-commercial-flight-plan-approvals", flightPlanDetail?.id],
    queryFn: () => fetchCommercialFlightPlanApprovals(flightPlanDetail?.id),
    enabled: !!flightPlanDetail?.id,
  });
  const escortArrivalStatus =
    commercialEscortService?.data?.escortService?.arrivalStatus ===
    ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.arrivalStatus;
  const escortDepartureStatus =
    commercialEscortService?.data?.escortService?.departureStatus ===
    ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.departureStatus;

  useEffect(() => {
    const storedData = localStorage.getItem("flightPlanData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      // Extract flight plan data from the nested structure
      const flightPlan = parsedData.cfp || parsedData;
      setFlightPlanDetail(flightPlan);
      setEditedFlightDetails({
        aircraftRegistrationNumber: flightPlan.aircraftRegistrationNumber,
        departureDelay: flightPlan.departureDelay ?? "00:00:00",
        arrivalDelay: flightPlan.arrivalDelay ?? "00:00:00",
        departureDelayFromArrivalAirport:
          flightPlan.departureDelayFromArrivalAirport ?? "00:00:00",
        passengerBoardingCount: flightPlan.scannedPassengerCount,
        differentCountReason: "",
      });
    }
  }, []);

  const openSpecialOnboarding = (type) => {
    const flightNumber = getFlightNumber(flightPlanDetail);
    navigate(
      ROUTE_PATHS.COMMERCIAL_AIRLINE_ESCORT_DETAILS.replace(
        ":flightNumber",
        flightNumber
      ).replace(":flightId", flightPlanDetail?.id)
    );
  };

  return (
    <div className="trip-details-container">
      <CommercialAirlineNavigationBar
        title={`${getFlightNumber(flightPlanDetail)} - ${getAirlineName(
          airlineData,
          flightPlanDetail?.airlineId
        )}`}
        backContent={
          <div className="navigation-header">
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
              className="navigation-header-link"
            >
              Home/
            </Link>
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_VIEW_SPECIAL_ONBOARDING}
              className="navigation-header-link"
            >
              Special Onboarding/
            </Link>
            {getFlightNumber(flightPlanDetail)}
          </div>
        }
      />
      {userData?.isAAUser &&
        (flightPlanDetail?.adsbVerified || flightPlanDetail?.bmsVerified) && (
          <div className="trip-details-verification-icon-divider-row">
            <VerificationIcon
              adsbVerified={flightPlanDetail?.adsbVerified}
              bmsVerified={flightPlanDetail?.bmsVerified}
            />
          </div>
        )}
      <TripDetailsTable
        isEditing={false}
        airportData={airportData}
        flightPlanDetail={flightPlanDetail}
        editedFlightDetails={editedFlightDetails}
        setEditedFlightDetails={setEditedFlightDetails}
      />
      {(userData.isAAUser ||
        flightPlanDetail?.disputeStatus ===
          DisputeStatus.AA_FORCED_BMS_COUNT) && (
        <div className="trip-details-bms-passenger-count-container">
          <div className="trip-details-bms-passenger-count-table">
            <div className="trip-details-bms-passenger-count-table-header">
              <div className="trip-details-bms-passenger-count-table-cell">
                BMS Total Boarded Pax
              </div>
              <div className="trip-details-bms-passenger-count-table-cell">
                BMS Exempted Pax
              </div>
              {infantPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  Infant
                </div>
              )}
              {militaryPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  Military
                </div>
              )}
              {governmentPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  Government
                </div>
              )}
              {diplomatsPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  Diplomat
                </div>
              )}
            </div>
            <div className="trip-details-bms-passenger-count-table-row">
              <div className="trip-details-bms-passenger-count-table-cell">
                <p
                  className={
                    flightPlanDetail?.disputeStatus === DisputeStatus.RAISED
                      ? "passenger-count-rejected"
                      : ""
                  }
                >
                  {flightPlanDetail?.scannedPassengerCount}
                </p>
              </div>
              <div className="trip-details-bms-passenger-count-table-cell">
                <p
                  className={
                    flightPlanDetail?.disputeStatus === DisputeStatus.RAISED
                      ? "passenger-count-rejected"
                      : ""
                  }
                >
                  {flightPlanDetail?.exemptedPassengerCount}
                </p>
              </div>
              {infantPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  <p
                    className={
                      flightPlanDetail?.disputeStatus === DisputeStatus.RAISED
                        ? "passenger-count-rejected"
                        : ""
                    }
                  >
                    {flightPlanDetail?.scannedInfant}
                  </p>
                </div>
              )}
              {militaryPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  <p
                    className={
                      flightPlanDetail?.disputeStatus === DisputeStatus.RAISED
                        ? "passenger-count-rejected"
                        : ""
                    }
                  >
                    {flightPlanDetail?.scannedMilitary}
                  </p>
                </div>
              )}
              {governmentPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  <p
                    className={
                      flightPlanDetail?.disputeStatus === DisputeStatus.RAISED
                        ? "passenger-count-rejected"
                        : ""
                    }
                  >
                    {flightPlanDetail?.scannedGovernment}
                  </p>
                </div>
              )}
              {diplomatsPassengerEnabled && (
                <div className="trip-details-bms-passenger-count-table-cell">
                  <p
                    className={
                      flightPlanDetail?.disputeStatus === DisputeStatus.RAISED
                        ? "passenger-count-rejected"
                        : ""
                    }
                  >
                    {flightPlanDetail?.scannedDiplomats}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="trip-details-bms-passenger-count-divider"></div>
        </div>
      )}
      <div className="trip-details-passenger-count-display-container">
        <div className="trip-details-passenger-count-table">
          <div className="trip-details-passenger-count-table-header">
            <div className="trip-details-passenger-count-table-cell">
              Airline Total Boarded Pax
            </div>
            <div className="trip-details-passenger-count-table-cell">
              Airline Exempted Pax
            </div>
            {infantPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
                Infant
              </div>
            )}
            {militaryPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
                Military
              </div>
            )}
            {governmentPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
                Government
              </div>
            )}
            {diplomatsPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
                Diplomat
              </div>
            )}
            <div className="trip-details-passenger-count-table-cell">
              Crew Members
            </div>
          </div>
          <div className="trip-details-passenger-count-table-row">
            <div className="trip-details-passenger-count-table-cell">
              {flightPlanDetail?.airlinePassengerCount}
            </div>
            <div className="trip-details-passenger-count-table-cell">
            {flightPlanDetail?.airlineExemptedPassengerCount}
            </div>
            {infantPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
              {flightPlanDetail?.airlineInfant}
              </div>
            )}
            {militaryPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
                {flightPlanDetail?.airlineMilitary}
              </div>
            )}
            {governmentPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
                {flightPlanDetail?.airlineGovernment}
              </div>
            )}
            {diplomatsPassengerEnabled && (
              <div className="trip-details-passenger-count-table-cell">
                {flightPlanDetail?.airlineDiplomats}
              </div>
            )}
            
            <div className="trip-details-passenger-count-table-cell">
              {flightPlanDetail?.deadheadCrews &&
              flightPlanDetail.deadheadCrews.length > 0 ? (
                <div className="trip-details-crew-members-list">
                  {flightPlanDetail.deadheadCrews.map((crew, index) => (
                    <div key={index} className="trip-details-crew-member-item">
                      <div className="trip-details-crew-member-name">
                        {crew.passengerName} - {crew.seatNumber}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>0</p>
              )}
            </div>
          </div>
        </div>
        <div className="trip-details-passenger-count-divider"></div>
      </div>
      {flightPlanApprovals?.data?.length >= 1 && (
        <TripDetailsApprovedView
          flightApprovalData={flightPlanApprovals?.data}
        />
      )}
      <div className="trip-details-details-container">
        <TripDetailsPaymentSummary
          flightPlanDetail={flightPlanDetail}
          requiresEscortService={flightPlanDetail?.requiresEscortService}
          isArrivalApproved={
            commercialEscortService?.data?.escortService?.arrivalStatus ===
              ClearanceStatus.NOT_REQUIRED ||
            escortArrivalStatus === ClearanceStatus.CLEARANCE_REJECTED ||
            escortArrivalStatus === ClearanceStatus.CANCELLED
          }
          isDepartureApproved={
            commercialEscortService?.data?.escortService?.departureStatus ===
              ClearanceStatus.NOT_REQUIRED ||
            escortDepartureStatus === ClearanceStatus.CLEARANCE_REJECTED ||
            escortDepartureStatus === ClearanceStatus.CANCELLED
          }
        />
        <TripDetailsSpecialOnboarding
          viewSpecialOnboarding={() => openSpecialOnboarding("view")}
          isAddedSpecialOnboarding={flightPlanDetail?.requiresEscortService}
        />
      </div>
    </div>
  );
};

export default TripDetails;
